// @ts-nocheck
import React, { useState, useEffect, useMemo } from "react";
import "chartjs-adapter-date-fns";
import ProcessedScatterGraph from "../forecastGraphs/ProcessedScatterGraph";
import { SkeletonChart } from "../Skeleton";
import ForecastAccuracySummary from "./ForecastAccuracySummary";
import ForecastIssuePicker from "./ForecastIssuePicker";
import {
  formatProcessedDataGraph,
  separatePercentileChannels,
} from "../../util/utils";
import { fetchAndParseForecastRecord } from "../../utils/modelOutputUtils";
import { readSwatch } from "../../utils/colorUtils";
import {
  customViewModelColor,
  fitPreviewWindow,
  historyWindow,
  listForecastIssues,
  loadIssueAccuracy,
  overlayAxisUnit,
  previewLegend,
  refetchRangeLines,
  timeSpan,
} from "../../utils/composedForecastUtils";
import type { CombinedPreviewData, ForecastIssue, HistoryAnchor, IssueAccuracy } from "../../utils/composedForecastUtils";
import { ACCURACY_METRICS } from "../../utils/forecastAccuracy";
import { applyChannelDisplay, DEFAULT_ERROR_COLOR } from "../../utils/composedForecastDisplay";
import { weightModelData } from "../../utils/composedForecastWeight";
import { pickModelKey } from "../../utils/seriesMetaData";

interface ComposedForecastCombinedChartProps {
  baseModel: CombinedPreviewData["baseModel"];
  overlayLines: CombinedPreviewData["overlayLines"];
  /** Observation source + scoring settings; when set, the base model is scored per forecast issue. */
  accuracy?: CombinedPreviewData["accuracy"];
  // When true, the chart fills its parent (h-full w-full) instead of using the built-in fixed heights. Use this on the asset dashboard where the parent is a flex container that should drive the size. Default false preserves the form-modal preview's fixed layout.
  fillContainer?: boolean;
  // The view's saved sources: given, a legend above the chart lists every one with its status; omitted where the parent has no room (the asset graph)
  legendSources?: any[];
  // Stacks the forecast-issue picker under a full-width legend instead of beside it (the Custom View modal)
  pickerBelowLegend?: boolean;
  /** Days of history before the forecast (the view's setting); unset = as long as the forecast. */
  historyDays?: number | null;
  /** What the history ends at: the selected forecast issue (default) or today. */
  historyAnchor?: HistoryAnchor | null;
}

const ComposedForecastCombinedChart: React.FC<
  ComposedForecastCombinedChartProps
> = ({
  baseModel,
  overlayLines,
  accuracy = null,
  fillContainer = false,
  legendSources,
  pickerBelowLegend = false,
  historyDays = null,
  historyAnchor = "issue",
}) => {
  const [loading, setLoading] = useState(!!baseModel);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<{
    percentiles: any;
    otherChannels: any;
    yUnit: string;
    title: string;
    modelData: any;
    /** The issue file this data came from, so the history window follows the drawn issue, not the picked one. */
    s3Key: string;
  } | null>(null);

  // Forecast issues of the base model, newest first; index 0 is the latest (baseModel.s3Key)
  const [issues, setIssues] = useState<ForecastIssue[]>([]);
  const [issueIndex, setIssueIndex] = useState(0);
  const [scored, setScored] = useState<IssueAccuracy | null>(null);
  const [scoring, setScoring] = useState(false);
  const [scoreError, setScoreError] = useState<string | null>(null);

  useEffect(() => {
    setIssues([]);
    setIssueIndex(0);
    if (!baseModel?.outputId) return;
    let cancelled = false;
    listForecastIssues(baseModel.outputId)
      .then((list) => {
        if (!cancelled) setIssues(list);
      })
      .catch((err) => console.error("Failed to list forecast issues:", err));
    return () => {
      cancelled = true;
    };
  }, [baseModel?.outputId]);

  const s3Key = issues[issueIndex]?.s3Key ?? baseModel?.s3Key;

  // Load base model data for the selected issue
  useEffect(() => {
    if (!baseModel || !s3Key) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { parsedData } = await fetchAndParseForecastRecord(s3Key);
        if (cancelled) return;

        // The source's chosen variable of a multi-variable output, else its first
        const models = parsedData?.models || {};
        const modelData = models[pickModelKey(models, baseModel.variation)];
        if (!modelData?.data) {
          setError("No data in forecast file");
          return;
        }

        // The source's weight multiplies the bands, the mean and what the accuracy score compares
        const weightedData = weightModelData(modelData.data, baseModel.weight);
        const formatted = formatProcessedDataGraph(weightedData);
        const { otherChannels, percentiles } =
          separatePercentileChannels(formatted);
        setGraphData({
          percentiles,
          otherChannels,
          yUnit: modelData.yUnit || "",
          title: baseModel.sourceName,
          modelData: weightedData,
          s3Key,
        });
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load base model");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [s3Key, baseModel?.weight, baseModel?.variation]);

  // Score the selected issue against the observation over the issue's own window
  useEffect(() => {
    setScored(null);
    setScoreError(null);
    if (!accuracy || !graphData?.modelData) return;
    let cancelled = false;
    setScoring(true);
    loadIssueAccuracy(accuracy, graphData.modelData)
      .then((res) => {
        if (!cancelled) setScored(res);
      })
      .catch((err) => {
        if (!cancelled) setScoreError(err?.message || "Failed to load observations");
      })
      .finally(() => {
        if (!cancelled) setScoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accuracy, graphData]);

  // The base model's display settings: hidden channels dropped, chosen colours passed through (memoised so the
  // memo'd ProcessedScatterGraph isn't handed new objects every render)
  const shown = useMemo(
    () =>
      graphData ? applyChannelDisplay(graphData.percentiles, graphData.otherChannels, baseModel?.display) : null,
    [graphData, baseModel?.display]
  );

  // The base model's own time span, once loaded, anchors the window every overlay is clipped to
  const modelRange = useMemo(() => {
    if (!graphData) return null;
    const times = [
      ...Object.values(graphData.percentiles || {}),
      ...Object.values(graphData.otherChannels || {}),
    ]
      .flat()
      .map((p: any) => new Date(p?.x).getTime())
      .filter(Number.isFinite);
    return timeSpan(times);
  }, [graphData]);

  const showLegend = !!legendSources?.length;

  // The history shown with the forecast, ending at the selected issue (or today); every line is read and clipped to it
  // Read from the issue whose file is drawn: the picker changes first, the file follows, and reading the picked
  // issue would build the window twice (and fetch the history twice) on every step
  const drawnIssue = graphData ? issues.find((issue) => issue.s3Key === graphData.s3Key) : undefined;
  const issuedAt = drawnIssue?.forecastIssued ? new Date(drawnIssue.forecastIssued).getTime() : null;
  const viewWindow = useMemo(
    () => (modelRange ? historyWindow(modelRange, { historyDays, anchor: historyAnchor, issuedAt }) : null),
    [modelRange, historyDays, historyAnchor, issuedAt]
  );

  // Range lines (a historical model's series, a curated dataset) were read for their newest data; once the base
  // issue's span is known they are read again for that window (stage 5, decision 2), and again when the issue changes
  const [rangeLines, setRangeLines] = useState<Map<string, (typeof overlayLines)[number]>>(new Map());
  useEffect(() => {
    if (!baseModel || !viewWindow || !overlayLines.some((line) => line.rangeRef || line.parameterId)) {
      setRangeLines(new Map());
      return;
    }
    // The previous window's lines stay up until the new ones land, so an issue change never flashes the unwindowed read
    let cancelled = false;
    refetchRangeLines(overlayLines, viewWindow)
      .then((lines) => {
        if (cancelled) return;
        setRangeLines(new Map(lines.filter((line) => line.rangeRef || line.parameterId).map((line) => [line.sourceId, line])));
      })
      .catch((err) => console.error("Failed to re-read range sources for the forecast's window:", err));
    return () => {
      cancelled = true;
    };
  }, [overlayLines, baseModel, viewWindow]);
  const windowedLines = useMemo(
    () => (rangeLines.size ? overlayLines.map((line) => rangeLines.get(line.sourceId) ?? line) : overlayLines),
    [overlayLines, rangeLines]
  );

  const fitted = useMemo(
    () => fitPreviewWindow(windowedLines, baseModel ? modelRange : null, viewWindow),
    [windowedLines, baseModel, modelRange, viewWindow]
  );

  // Clipped overlays; when scored, the observation follows the selected issue's window and the error series goes on the right axis
  const chartOverlays = useMemo(() => {
    if (!accuracy || !scored) return fitted.overlayLines;
    // The observation is redrawn from the scored window, so it comes back even if its stale records were clipped out
    const clippedOut = windowedLines.filter(
      (line) => line.isObservation && !fitted.overlayLines.some((f) => f.name === line.name)
    );
    // The scored window replaces the observation's points inside the forecast; its history before the forecast stays
    const forecastStart = modelRange?.start ?? Infinity;
    const lines = [...fitted.overlayLines, ...clippedOut].map((line) =>
      line.isObservation
        ? {
            ...line,
            records: [
              ...line.records.filter((r) => new Date(r.timestamp).getTime() < forecastStart),
              ...scored.observations
                .filter((o) => o.t >= forecastStart)
                .map((o) => ({ timestamp: new Date(o.t), value: o.value })),
            ],
          }
        : line
    );
    const plotDef = ACCURACY_METRICS.find((m) => m.id === accuracy.config.plotMetric);
    if (plotDef && scored.result.series.length) {
      const unit = plotDef.unit === "unit" ? graphData?.yUnit : plotDef.unit === "unit²" ? `(${graphData?.yUnit})²` : plotDef.unit;
      const axisLabel = unit ? `${plotDef.label} (${unit})` : plotDef.label;
      lines.push({
        name: plotDef.label,
        records: scored.result.series.map((p) => ({ timestamp: new Date(p.t), value: p.value })),
        color: accuracy.errorColor || DEFAULT_ERROR_COLOR,
        axis: "right",
        axisLabel,
        // Same unit as the forecast (error, absolute error): share the left axis's scale so they read alike
        sharedScale: plotDef.unit === "unit",
      });
    }
    return lines;
  }, [fitted, windowedLines, accuracy, scored, graphData?.yUnit, modelRange]);

  // No forecast in the view: the same chart draws the lines alone, so every view reads alike (axis, unit, grid)
  const linesOnly = !baseModel && fitted.overlayLines.length > 0;
  const EMPTY = useMemo(() => ({}), []);

  // Where the parent gives no source list (the asset graph), a compact legend of what is drawn names the lines
  const drawnLegend = useMemo(() => {
    if (showLegend) return [];
    const items: Array<{ id: string; name: string; color: string }> = [];
    if (graphData && baseModel) items.push({ id: "base", name: baseModel.sourceName, color: baseModel.display?.channels?.mean?.color || customViewModelColor() });
    chartOverlays.forEach((line, i) => items.push({ id: `${line.sourceId}-${i}`, name: line.name, color: line.color }));
    return items;
  }, [showLegend, graphData, baseModel, chartOverlays]);

  const legendItems = showLegend
    ? previewLegend(legendSources, {
        loadedLines: windowedLines,
        // What the chart actually draws, so a scored observation brought back into the window isn't listed as missing
        fit: {
          overlayLines: chartOverlays,
          outside: fitted.outside.filter((o) => !chartOverlays.some((line) => line.sourceId === o.sourceId)),
        },
        baseModelDrawn: !!(graphData && baseModel),
        baseModelBanded: !!graphData && Object.keys(graphData.percentiles || {}).length > 0,
      })
    : [];

  // Colour + name, plus "daily averages" when downsampled; the type ("Model output · shaded range") is in the hover
  // title. The saved-source list when the parent gives one, else what is drawn — one row either way.
  const showPicker = issues.length > 1;
  const rows = showLegend ? legendItems : drawnLegend.map((item) => ({ ...item, note: "", dim: false, averaged: null }));
  const legend = rows.length > 0 ? (
    <div className={`flex min-w-0 flex-1 flex-wrap gap-x-4 gap-y-1.5 ${showPicker && !pickerBelowLegend ? "sm:justify-end" : ""} font-inter text-eai-body-xs`}>
      {rows.map((item) => (
        <span key={item.id} className="flex min-w-0 items-center gap-1.5" title={item.note ? `${item.name} — ${item.note}` : item.name}>
          <span
            aria-hidden
            className={`h-2 w-2 shrink-0 rounded-full ${item.dim ? "opacity-40" : ""}`}
            style={{ backgroundColor: item.color }}
          />
          <span className={`truncate ${item.dim ? "text-eai-ink-faint" : "text-eai-ink"}`}>{item.name}</span>
          {item.averaged && <span className="whitespace-nowrap text-eai-ink-faint">· {item.averaged}</span>}
        </span>
      ))}
    </div>
  ) : null;

  // Picker first (top on phones, top-left on desktop) with the legend right-aligned beside it; or stacked under the legend
  const controlRow = (picker?: React.ReactNode) =>
    (legend || picker) && (
      <div className={`mb-4 flex shrink-0 flex-col gap-2 ${pickerBelowLegend ? "" : "sm:flex-row sm:items-start sm:gap-4"}`}>
        {legend}
        {picker && <div className={`w-full shrink-0 sm:w-[320px] ${pickerBelowLegend ? "" : "order-first"}`}>{picker}</div>}
      </div>
    );

  if (loading && !graphData) {
    return <SkeletonChart bars={16} label="Loading combined preview" withHeader={false} />;
  }

  // The legend still lists every source when the chart can't draw, so the view's makeup never disappears
  // Every case shares this frame: with fillContainer it's the flex column the chart's flex-1 grows in. Case 2 and the
  // messages used to return a bare fragment, so the chart sat at its min height in the asset dashboard.
  const frameClass = fillContainer ? "flex h-full w-full flex-col" : "w-full";

  if (error) {
    return (
      <div className={frameClass}>
        {controlRow()}
        <div className="py-6 text-center font-inter text-eai-body-sm text-eai-danger-text">
          {error}
        </div>
      </div>
    );
  }

  // Case 1: Base model exists → ProcessedScatterGraph with overlay lines
  if (graphData && baseModel && shown) {
    const chart = (
      <ProcessedScatterGraph
        key={s3Key}
        percentiles={shown.percentiles}
        otherChannels={shown.otherChannels}
        channelColors={shown.channelColors}
        bandColors={shown.bandColors}
        yaxis={graphData.yUnit || "Value"}
        timeZone={"UTC"}
        assetTimeZone={"UTC"}
        timeZoneMode={"utc"}
        title={graphData.title}
        additionalDataSources={chartOverlays.length > 0 ? chartOverlays : null}
        hideGraphControls
        hideMobileControls={false}
        meanColor={customViewModelColor()}
        meanLabel={baseModel.sourceName}
        nowLineColor={readSwatch("text-eai-ink-faint")}
        baseLayer={baseModel.layer}
      />
    );

    return (
      <div className={frameClass}>
        {controlRow(showPicker && <ForecastIssuePicker issues={issues} index={issueIndex} onChange={setIssueIndex} />)}
        <div className={fillContainer ? "h-[260px] sm:h-auto sm:min-h-[240px] sm:flex-1" : "h-[280px] w-full sm:h-[370px]"}>{chart}</div>
        {accuracy && (
          <ForecastAccuracySummary
            config={accuracy.config}
            result={scored?.result ?? null}
            observationName={accuracy.sourceName}
            observationIssue={scored?.observationIssue}
            unit={graphData.yUnit}
            loading={scoring || loading}
            error={scoreError}
          />
        )}
      </div>
    );
  }

  // Case 2: No base model → the same chart with only the lines
  if (linesOnly) {
    return (
      <div className={frameClass}>
        {controlRow()}
        <div className={fillContainer ? "h-[260px] w-full sm:h-auto sm:min-h-[240px] sm:flex-1" : "h-[280px] w-full sm:h-[370px]"}>
          <ProcessedScatterGraph
            percentiles={EMPTY}
            otherChannels={EMPTY}
            yaxis={overlayAxisUnit(fitted.overlayLines)}
            timeZone={"UTC"}
            assetTimeZone={"UTC"}
            timeZoneMode={"utc"}
            title="Custom View"
            additionalDataSources={fitted.overlayLines}
            hideGraphControls
            hideMobileControls={false}
            nowLineColor={readSwatch("text-eai-ink-faint")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={frameClass}>
      {controlRow()}
      <div className="py-6 text-center font-inter text-eai-body-sm text-eai-ink-faint">
        No previewable data sources
      </div>
    </div>
  );
};

export default ComposedForecastCombinedChart;
